import * as z from "zod";
import * as fetchers from './fetchers.js'
import { api } from './api.js'
import * as events from './events.js'

export const CardSchema = z.object({
    focus: z.string(),
    type: z.string(),
    prompt: z.string(),
    phrase: z.string(),
    acceptedAnswers: z.array(z.string()),
    translations: z.array(z.string()),
    ipa: z.string(),
    sound: z.number().nullable()
})

export type CardZod = z.infer<typeof CardSchema>;
// export type Repetition = z.infer<typeof RepetitionSchema>;
export type Queue = z.infer<typeof QueueSchema>;
export type Status = z.infer<typeof StatusSchema>;

export type CompletedRep = {
    repId: number,
    startTime: number,
    endTime: number,
    passedAutoCheck: boolean,
    answerInput: string,
    card: Card
}

export type Rating = {
    repId: number,
    rating: number,
    time: number
}

export type AnswerCard = {
    repId: number,
    focus: string,
    translations: string[]
}

export const RepetitionSchema = z.object({
    id: z.number(),
    stage: z.string(),
    card: CardSchema
})

export const QueueSchema = z.object({
    newSkills: z.array(RepetitionSchema)
})

const RepCount = z.object({
    total: z.number(),
    new: z.number(),
    reviews: z.number()
})

export const StatusSchema = z.object({
    done: RepCount,
    due: RepCount,
    doneAny: z.boolean(),
    anyNewSkillIsDue: z.boolean(),
    anyReviewIsDue: z.boolean(),
    anyIsDue: z.boolean(),
    doneAll: z.boolean()
})





export class CardBack {
    constructor(
        public focus: string,
        public translations: Array<string>
    ) {
        this.focus = focus
        this.translations = translations
    }

    get has_audio(): boolean {
        return false
    }
}

export class Card {
    #audio; #acceptedAnswers; #translations
    public focus: string
    public type: string
    public prompt: string
    public phrase: string
    public ipa: string
    public sound_id: number|null

    constructor(
        card:CardZod,
        audio:HTMLAudioElement|null
    ) {
        this.focus = card.focus
        this.type = card.type
        this.prompt = card.prompt
        this.phrase = card.phrase
        this.#acceptedAnswers = card.acceptedAnswers
        this.#translations = card.translations
        this.ipa = card.ipa
        this.sound_id = card.sound
        this.#audio = audio
    }

    static async create(card:CardZod): Promise<Card> {
        if (card.sound) {
            const audio = await Card.get_audio(card.sound)
            return new Card(card, audio)
        } else {
            return new Card(card, null)
        }
    }

    createCardBack(): CardBack {
        return new CardBack(this.focus, this.#translations)
    }

    static async get_audio(audio_id:number): Promise<HTMLAudioElement|null> {
        return await fetchers.fetchAudio(audio_id)
    }

    play_audio(): void {
        if (this.#audio) {
            this.#audio.play()
        }
    }

    check_answer(answer: string): boolean {
        return this.#acceptedAnswers.includes(answer)
    }

    get has_audio(): boolean {
        return Boolean(this.#audio)
    }
}

export class Repetition {
    #startTime:number|null = null
    #endTime:number|null = null
    #answer:string|null = null
    #autoCheckOverruled: boolean = false
    #rating: number|null = null
    #ratingTime: number|null = null
    constructor(
        public id:number,
        public stage:string,
        public card:Card) {
        this.id = id;
        this.stage = stage;
        this.card = card;
    }

    start(): void {
        this.#startTime = Date.now()
    }

    end(): void {
        this.#endTime = Date.now()
    }

    answer(answer: string): void {
        this.#answer = answer
    }

    checkAnswer(): boolean {
        if (this.#answer) {
            return this.card.check_answer(this.#answer)
        } else {
            return false
        }
    }

    get answerIsCorrect(): boolean {
        if (this.#autoCheckOverruled) {
            return this.#autoCheckOverruled
        } else {
            return this.checkAnswer()
        }
    }

    get skillStageLabel(): string {
        if (this.stage == 'new') {
            return 'Nova habilidade'
        } else if (this.stage == 'review') {
            return 'Revisão'
        } else {
            throw Error("Skill stage not recognized")
        }
    }

    get answeredDontKnow(): boolean {
        return this.#answer == ""
    }

    async save() {
        if (this.#answer == null) {
            throw Error("Can't save Rep before answer is provided.")
        }
        const data = {
            repId: this.id,
            startTime: this.#startTime,
            endTime: this.#endTime,
            answerInput: this.#answer,
            passedAutocheck: this.checkAnswer()
        }
        // console.log('saving', data)
        return await api.post('/rep/complete', data)
            .catch(error => {
                const event = new events.ActionFailed(
                    'A última resposta não foi salva, porque houve um erro.',
                    error
                )
                document.dispatchEvent(event)
                console.log('Full error:', error.response?.data); // Check server response
                console.log('Status:', error.response?.status);
                console.log('Message:', error.message);
            });
    }

    get resultCode(): string {
        if (this.#answer == null) {
            throw Error(`Can't provide result code for Rep ${this.id} because it has not been answered.`)
        } else if (this.#answer == "") {
            return "-1"
        } else if (this.answerIsCorrect) {
            return "1"
        } else if (this.#answer && !this.answerIsCorrect) {
            return "0"
        } else {
            throw Error(`Couldn't get result code for Rep ${this.id} for an unknown reason.`)
        }
    }

    overruleCheck() { 
        this.#autoCheckOverruled = true
    }

    async rate(rating: events.RatingEvent) {
        if (!this.answerIsCorrect) {
                throw Error("Can't rate Rep ${this.id} because the answer was not correct.")
            }
        this.#rating = Number(rating.rating)
        this.#ratingTime = rating.time
        const data = {
            repId: this.id,
            rating: rating.rating,
            ratingTime: rating.time,
            userOverruledAutocheck: this.#autoCheckOverruled
        }
        // console.log('rating', data)
        await api.post('/rep/rate', data)
            .catch(error => {
                const event = new events.ActionFailed(
                    'A última escolha não foi salva, porque houve um erro.',
                    error
                )
                document.dispatchEvent(event)
                console.log('Full error:', error.response?.data); // Check server response
                console.log('Status:', error.response?.status);
                console.log('Message:', error.message);
            });
    }

    async changeRating(rating: events.RatingEvent) {
        if (!this.#rating) {
            throw Error(`Can't change rating for repetition ${this.id} because it does not have a rating.`)
        }
        if (Number(rating.rating) == this.#rating) {
            return
        }

        this.#rating = Number(rating.rating)
        this.#ratingTime = rating.time
        const data = {
            repId: this.id,
            rating: rating.rating,
            ratingTime: rating.time,
        }
        // console.log('rating', data)
        await api.post('/rep/change-rating', data)

    }

    async confirmFailure() {
        if (this.checkAnswer()) {
            throw Error (`Can't confirm failure of Rep ${this.id} because the answer seems correct.`)
        }
        const data = {
            repId: this.id,
            rating: 1,
            ratingTime: Date.now(),
            userOverruledAutocheck: false
        }
        // console.log('confirming failure rating', data)
        await api.post('/rep/rate', data)
    }

}




export class RepSession {
    private reps: Record<number, Repetition>

    constructor() {
        this.reps = {}
    }

    static async create() {
        try {
            await api.post('/reps/initialize', {time: Date.now()})
            return new RepSession()
        } catch (error) {
            // console.error('Failed:', error.message);
        }
    }

    getRepById(id: number): Repetition {
        return this.reps[id]
    }

    async next(): Promise<Repetition> {
        const response = await api.get('/reps/next')
        const rep_data = response.data
        const card_data = rep_data.card
        const card = await Card.create(card_data)
        const rep = new Repetition(rep_data.id, rep_data.stage, card)
        this.reps[rep.id] = rep
        return rep
    }

    async start() {
        await api.post('/reps/start', {time: Date.now()})
        window.addEventListener('beforeunload', () => {
            const blob = new Blob([JSON.stringify({time: Date.now()})], {type: 'application/json'});
            navigator.sendBeacon('/api/reps/end', blob);
        });
    }

    async end() {
        await api.post('/reps/end', {time: Date.now()})
        // REMOVE beforeunload event lister from window
    }
}